import axios from "axios";
import authentificatedHttp from "./http";

const API_URL = process.env.REACT_APP_API_URL;

interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
  captchaToken: string;
  fingerprintVisitorId?: string;
  fingerprintConfidence?: number;
}

interface TwoFactorCredentials {
  token: string;
  rememberMe: boolean;
  captchaToken: string;
  fingerprintVisitorId?: string;
  fingerprintConfidence?: number;
}


const http = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const authApi = {
  login: async (credentials: LoginCredentials) => {
    const response = await http.post(`${API_URL}/auth/login`, credentials);
    return response;
  },

  verify2FA: async (credentials: TwoFactorCredentials) => {
    const tempToken = localStorage.getItem('tempToken');
    const response = await http.post(`${API_URL}/auth/verify-2fa`, credentials, {
      headers: { Authorization: `Bearer ${tempToken}` }
    });
    return response.data;
  },

  // Schimbă un token de impersonare (generat din ERP-ul TLS) pe sesiune.
  impersonate: async (
    token: string,
    extra?: { fingerprintVisitorId?: string; fingerprintConfidence?: number },
  ) => {
    const response = await http.post(`${API_URL}/auth/impersonate`, { token, ...extra });
    return response.data.data ?? response.data;
  },

  trackPage: async (path: string, sessionId?: string | null) => {
    const response = await authentificatedHttp.post(`${API_URL}/auth/session/page`, {
      path,
      sessionId: sessionId || undefined,
    });
    return response.data;
  },

  logout: async () => {
    const response = await axios.post(`${API_URL}/auth/logout`, {}, {
      headers: { Authorization: `Bearer ${localStorage.getItem('refreshToken')}` }
    });
    return response.data;
  },

  // sessionId îi spune backend-ului dacă sesiunea e o impersonare din ERP (turul ghidat nu pornește atunci)
  me: async () => {
    const response = await authentificatedHttp.post(`${API_URL}/auth/me`, {
      sessionId: localStorage.getItem('sessionId') || undefined,
    });
    return response.data;
  },

  // Turul ghidat s-a încheiat (parcurs sau sărit) — backend-ul scrie data doar prima dată
  markOnboardingTour: async (outcome: 'completed' | 'skipped') => {
    const response = await authentificatedHttp.post(`${API_URL}/user-settings/onboarding-tour`, {
      outcome,
      sessionId: localStorage.getItem('sessionId') || undefined,
    });
    // backend-ul învelește răspunsurile în { data } (interceptorul global)
    return (response.data?.data ?? response.data) as { saved: boolean; onboardingTourCompletedAt: string | null };
  },

  checkGoogleLogin: async (token: string) => {
    const response = await http.post(`${API_URL}/auth/google`, { token });
    return response.data;
  },

  checkFacebookLogin: async (token: string) => {
    const response = await http.post(`${API_URL}/auth/facebook`, { token });
    return response.data;
  },

  // Setarea parolei inițiale pe baza tokenului de activare din emailul de invitație.
  setPassword: async (token: string, password: string) => {
    const response = await http.post(`${API_URL}/auth/set-password`, { token, password });
    return response.data;
  },

  // Cerere de resetare parolă (trimite email cu link).
  forgotPassword: async (email: string) => {
    const response = await http.post(`${API_URL}/auth/forgot-password`, { email });
    return response.data;
  },

  // Resetarea parolei pe baza tokenului primit pe email.
  resetPassword: async (token: string, password: string) => {
    const response = await http.post(`${API_URL}/auth/reset-password`, { token, password });
    return response.data;
  },

  // Preferințele de notificare ale contului (autentificat).
  getPreferences: async () => {
    const response = await authentificatedHttp.get(`${API_URL}/user-settings/preferences`);
    return response.data;
  },

  updatePreferences: async (prefs: { notifyOnNewLogin?: boolean }) => {
    const response = await authentificatedHttp.patch(`${API_URL}/user-settings/preferences`, prefs);
    return response.data;
  },

  changeAvatar: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await authentificatedHttp.post(`${API_URL}/user-settings/change-avatar`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteAvatar: async () => {
    const response = await authentificatedHttp.delete(`${API_URL}/user-settings/delete-avatar`);
    return response.data;
  }

};